import React, {useLayoutEffect, useContext, useState} from 'react';
import {useBreakpoint} from 'polyphony/hooks';
import {times, lcm, uuidv4} from 'polyphony/util';
import {prefixCSS} from 'polyphony/css';
import {isIE} from 'polyphony/browser';

/*
   Masonry

   A component capable of vertically rendering arbitrarily sized masonry
   elements.

   <Masonry unitsWide={4}>
       <Masonry.Tile id="a" widthUnits={3} heightUnits={1} />
       <Masonry.Tile id="b" widthUnits={1} heightUnits={1} />
   </Masonry>

   would yield:

   +---+---+---+---+
   | a | a | a | b |
   +---+---+---+---+

   <Masonry unitsWide={4}>
       <Masonry.Tile id="a" widthUnits={1} heightUnits={2} />
       <Masonry.Tile id="b" widthUnits={1} heightUnits={1} />
       <Masonry.Tile id="c" widthUnits={1} heightUnits={1} />
   </Masonry>

   would yield:

   +---+---+
   | a | b |
   +---+---+
   | a | c |
   +---+---+

   More complex layouts will fit tiles in like tetris, adhering
   strictly to the following rules:
   1. Tiles are placed starting on the shortest possible column, in left to right order.
   1b. Ties are broken by picking the leftmost column.
   2. Tiles are only placed in spaces in the masonry that will not overlap.
   3. Tiles are always placed in the _next_ available spot
   3b. new tiles won't fill in gaps retroactively.

   Implementation:

   The Masonry component provides a context to its children.
   The context contains functions for registering and
   deregistering Masonry.Tile elements.

   Masonry.Tile elements defer rendering of the real masonry tile
   (InternalTile) until after they are mounted in the VDOM. Once
   a Masonry.Tile is mounted, it renders its real content, stores it
   internally, and registers it with the closest ancestor Masonry element.
   useEffect() is used to avoid infinite loops caused by calling setJSX
   in the main render function that defined it.

   Once a Masonry.Tile element is un-mounted, it deregisters its JSX
   with the ancestor Masonry element & frees the JSX from memory.
*/

const MasonryContext = React.createContext();

class OrderedBag {
    constructor() {
        this.keys = [];
        this.values = {};
    }

    toArray() {
        return this.keys.map(k => this.values[k]);
    }

    contains(key) {
        return key in this.keys;
    }

    add(value) {
        let k = uuidv4();
        this.keys.push(k);
        this.values[k] = value;
        return k;
    }

    remove(key) {
        let idx = this.keys.indexOf(key);
        if (idx !== -1) {
            this.keys.splice(idx, 1);
            delete this.values[key];
        }
    }
}

const Masonry = ({unitsWide,
                  dynamicMobile, // If false, mobile tiles are all 1x1. Otherwise, they
                                 // will have a width of 1 unit, but will keep their aspect ratio
                  style, ...props}) => {
    const isMobile = useBreakpoint() === 'mobile';

    const [colsWide, setColsWide] = useState(0);
    const [tiles, setTiles] = useState(new OrderedBag());

    const refreshTiles = () => {
        let tileArray = tiles.toArray();
        let [cw, positioning] = calculateTilePositioning(tileArray, isMobile, dynamicMobile, unitsWide);
        setColsWide(cw);
        setTiles(tiles);

        for (let i = 0; i < tileArray.length; i++) {
            let tile = tileArray[i];
            tile.setPositionProps(positioning[i]);
        }
    };

    const addTile = tile => {
        let k = tiles.add(tile);
        refreshTiles();
        return k;
    };

    const removeTile = k => {
        tiles.remove(k);
        refreshTiles();
    };

    let gridStyles = prefixCSS({
        display: 'grid',
        overflow: 'visible',
        gridTemplateColumns: times(colsWide, `calc(100% / ${colsWide})`).join(' '),
        gridColumns: times(colsWide, `calc(100% / ${colsWide})`).join(' ') // IE
    });

    return <MasonryContext.Provider value={{addTile, removeTile, dynamicMobile, colsWide}}>
               <div {...props} role="none" style={{
                   ...gridStyles,
                   ...style
               }} />
          </MasonryContext.Provider>;
};

Masonry.unitsToDecimal = units => {
    let [num, denom] = splitFrac(units);
    return num / denom;
};

Masonry.defaultProps = {
    unitsWide: 4,
    dynamicMobile: true
};

Masonry.Tile = ({dynamicMobile, widthUnits, heightUnits, children, ...props}) => {
    const isMobile = useBreakpoint() === 'mobile';
    const {addTile, removeTile, colsWide} = useContext(MasonryContext);

    const [isPositioned, setPositioned] = useState(false);
    const [row, setRow] = useState();
    const [column, setColumn] = useState();
    const [w, setW] = useState();
    const [h, setH] = useState();

    const setPositionProps = (props) => {
        if (!props) {
            setPositioned(false);
        } else {
            setPositioned(true);
            const {row, column, w, h} = props;
            setRow(row);
            setColumn(column);
            setW(w);
            setH(h);
        }
    };

    useLayoutEffect(() => {
        let k = addTile({setPositionProps, widthUnits, heightUnits});
        return () => {
            removeTile(k);
            setPositionProps(null);
        };
    }, [dynamicMobile, widthUnits, heightUnits, isMobile, colsWide, addTile, removeTile]);

    if (!isPositioned) return null;

    // Notice how we're rendering a plain old div?
    // Once we've calculated our position, there is no need to
    // involve JavaScript. Let CSS take care of this one.

    return <div role="none" style={prefixCSS({gridRow: isIE() ? `${row + 1}` : `${row + 1} / ${row + 1 + h}`,
                                  gridColumn: isIE() ? `${column + 1}` : `${column + 1} / ${column + 1 + w}`,
                                  // IE
                                  gridRowSpan: h,
                                  gridColumnSpan: w})} {...props}>
        <div role="none" style={prefixCSS({paddingBottom: `${100 * (h / w)}%`,
                               boxSizing: 'border-box',
                               height: '0',
                               position: 'relative'})}>
            <div role="none" style={prefixCSS({position: 'absolute', top: 0, bottom: 0, left: 0, right: 0})}>
                {children}
            </div>
        </div>
    </div>;
};

Masonry.Tile.displayName = "Masonry.Tile";

Masonry.Tile.defaultProps = {
    widthUnits: 1,
    heightUnits: 1
};

// Splits a fraction string into numerator and denominator.
// returns [numerator, denominator];
function splitFrac(x) {
    if (typeof x === 'string') {
        let splt = x.split(/\s*\/\s*/);
        if (splt.length === 2) {
            return [parseInt(splt[0]), parseInt(splt[1])];
        }
    }
    return [parseInt(x), 1];
}

function calculateTileSizing(widthUnits, heightUnits, isMobile = false, dynamicMobile = false) {
    // If we're just mobile, we're just going to be 1x1...
    if (isMobile && !dynamicMobile) return {widthNumerator: 1, widthDenominator: 1, heightNumerator: 1, heightDenominator: 1};

    let [widthNumerator, widthDenominator] = splitFrac(widthUnits);
    let [heightNumerator, heightDenominator] = splitFrac(heightUnits);

    // If we're dynamic mobile, we do some math to make the width 1,
    // but keep the height proportional
    if (isMobile && dynamicMobile) {
        heightNumerator = heightNumerator * widthDenominator;
        heightDenominator = heightDenominator * widthNumerator;
        widthNumerator = 1;
        widthDenominator = 1;
    }

    return {widthNumerator, widthDenominator, heightNumerator, heightDenominator};
}

function calculateTilePositioning(tiles, isMobile, dynamicMobile, unitsWide) {
    // 1. Calculate how many grid units equals one masonry unit
    let xfactor = 1;
    let yfactor = 1;

    for (let t of tiles) {
        const {widthUnits, heightUnits} = t;
        let {widthDenominator, heightDenominator} = calculateTileSizing(widthUnits, heightUnits, isMobile, dynamicMobile);
        xfactor = lcm(xfactor, widthDenominator);
        yfactor = lcm(yfactor, heightDenominator);
    }

    let factor = Math.max(yfactor, xfactor);

    // 2. Initialize data structures
    let colsWide = isMobile ? factor : factor * unitsWide;
    let columns = times(colsWide, 0);

    // 3. Compute where each block should go
    let positioning = [];
    for (let t of tiles) {
        const {widthUnits, heightUnits} = t;
        const {
            widthNumerator,
            widthDenominator,
            heightNumerator,
            heightDenominator
        } = calculateTileSizing(widthUnits, heightUnits, isMobile, dynamicMobile);

        let h = heightNumerator * (factor / heightDenominator);
        let w = widthNumerator * (factor / widthDenominator);
        let {row, column} = placeTile(w, h, columns);
        positioning.push({row, column, h, w});
    }

    return [colsWide, positioning];
}

function placeTile(widthUnits, heightUnits, columns) {
    //
    // placeTile()
    // This is the magic that lays out masonry.
    // Takes:
    // widthUnits: number of columns the tile will span
    // heightUnits: number of rows the tile will span
    // columns: state of the masonry, list of row numbers
    //
    // Returns: The row and column that can accommodate the tile,
    //          using an object of form: {row, column}
    //
    // Causes: Updates `columns` to reflect the placement of the tile.

    // Avoid placing an unplaceable tile.
    if (widthUnits > columns.length) {
        return undefined;
    }

    // This is not really an infinite loop (well, it technically is).
    // The algorithm will always increment the shortest column, so
    // eventually, an iteration will, in step #2, always set `fits` to true,
    // since `columns` will contain the same number, `columns.length` times.
    while (true) {
        // 1. Find the shortest column. This is where we
        //    will attempt to fit the tile.
        let shortest = columns.map((x, idx) => ({x, idx}));
        shortest.sort(({x: a}, {x: b}) => a - b);
        shortest = shortest.map(({idx}) => idx);

        let column = shortest[0];
        let row = columns[column];

        // 2. Calculate if the tile fits.

        // 2a. Test if the tile is wider than the masonry
        //     available to its right. This might let us skip
        //     the loop below.
        let fits = column + widthUnits <= columns.length;

        if (fits) {
            // 2b. Test if the column we're evaluating
            //     has taller neighbors which would prevent
            //     the tile we're trying to fit from fitting.
            for (let i = 0; i < widthUnits; i++) {
                fits = columns[column] >= columns[column + i];
                if (!fits) break;
            }
        }

        // 3. Reflect tile placement in `columns`.

        if (fits) {
            // 3a. Mark the appropriate area as filled.
            for (let i = 0; i < widthUnits; i++) {
                columns[column + i] += heightUnits;
            }
            return {row, column};
        }

        // 3b. Mark just the single cell pointed to by `row` & `column`
        //     as filled - our tile won't fit here.
        columns[column] += 1;
    }
}

export default Masonry;

